"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EIP1559FeeMarketTransaction = void 0;
const utils_1 = require("@ganache/utils");
const ethereum_address_1 = require("@ganache/ethereum-address");
const rlp_1 = require("@ganache/rlp");
const runtime_transaction_1 = require("./runtime-transaction");
const access_lists_1 = require("./access-lists");
const signing_1 = require("./signing");
const ethereum_utils_1 = require("@ganache/ethereum-utils");
const bigIntMin = (...args) => args.reduce((m, e) => (e < m ? e : m));
const CAPABILITIES = [2718, 2930, 1559];
class EIP1559FeeMarketTransaction extends runtime_transaction_1.RuntimeTransaction {
    constructor(data, common, extra) {
        super(data, common, extra);
        this.type = utils_1.Quantity.from("0x2");
        if (Array.isArray(data)) {
            this.chainId = utils_1.Quantity.from(data[0]);
            this.nonce = utils_1.Quantity.from(data[1]);
            this.maxPriorityFeePerGas = utils_1.Quantity.from(data[2]);
            this.maxFeePerGas = utils_1.Quantity.from(data[3]);
            this.gas = utils_1.Quantity.from(data[4]);
            this.to = data[5].length == 0 ? null : ethereum_address_1.Address.from(data[5]);
            this.value = utils_1.Quantity.from(data[6]);
            this.data = utils_1.Data.from(data[7]);
            const accessListData = access_lists_1.AccessLists.getAccessListData(data[8]);
            this.accessList = accessListData.accessList;
            this.accessListJSON = accessListData.AccessListJSON;
            this.accessListDataFee = accessListData.dataFeeEIP2930;
            this.v = utils_1.Quantity.from(data[9]);
            this.r = utils_1.Quantity.from(data[10]);
            this.s = utils_1.Quantity.from(data[11]);
            this.raw = [this.type.toBuffer(), ...data];
            if (!extra) {
                // TODO(hack): we use the presence of `extra` to determine if this data
                // come from the "database" or not. Transactions that come from the
                // database must not be validated since they may come from a fork.
                if (common.chainId() !== this.chainId.toBigInt()) {
                    throw new ethereum_utils_1.CodedError(`Invalid chain id (${this.chainId.toNumber()}) for chain with id ${common.chainId()}.`, utils_1.JsonRpcErrorCode.INVALID_INPUT);
                }
                const { from, serialized, hash, encodedData, encodedSignature } = this.computeIntrinsics(this.v, this.raw);
                this.from = from;
                this.serialized = serialized;
                this.hash = hash;
                this.encodedData = encodedData;
                this.encodedSignature = encodedSignature;
            }
        }
        else {
            if (data.chainId) {
                this.chainId = utils_1.Quantity.from(data.chainId);
            }
            else {
                this.chainId = utils_1.Quantity.from(common.chainId());
            }
            this.maxPriorityFeePerGas = utils_1.Quantity.from(data.maxPriorityFeePerGas);
            this.maxFeePerGas = utils_1.Quantity.from(data.maxFeePerGas);
            const accessListData = access_lists_1.AccessLists.getAccessListData(data.accessList);
            this.accessList = accessListData.accessList;
            this.accessListJSON = accessListData.AccessListJSON;
            this.accessListDataFee = accessListData.dataFeeEIP2930;
            this.validateAndSetSignature(data);
        }
    }
    maxGasPrice() {
        return this.maxFeePerGas;
    }
    toJSON(_common) {
        return {
            type: this.type,
            hash: this.hash,
            chainId: this.chainId,
            nonce: this.nonce,
            blockHash: this.blockHash ? this.blockHash : null,
            blockNumber: this.blockNumber ? this.blockNumber : null,
            transactionIndex: this.index ? this.index : null,
            from: this.from,
            to: this.to,
            value: this.value,
            maxPriorityFeePerGas: this.maxPriorityFeePerGas,
            maxFeePerGas: this.maxFeePerGas,
            gasPrice: this.effectiveGasPrice,
            gas: this.gas,
            input: this.data,
            accessList: this.accessListJSON,
            v: this.v,
            r: this.r,
            s: this.s
        };
    }
    static fromTxData(data, common, extra) {
        return new EIP1559FeeMarketTransaction(data, common, extra);
    }
    toVmTransaction() {
        const data = this.data.toBuffer();
        return {
            hash: () => utils_1.BUFFER_32_ZERO,
            nonce: this.nonce.toBigInt(),
            maxPriorityFeePerGas: this.maxPriorityFeePerGas.toBigInt(),
            maxFeePerGas: this.maxFeePerGas.toBigInt(),
            gasLimit: this.gas.toBigInt(),
            to: this.to,
            value: this.value.toBigInt(),
            data,
            AccessListJSON: this.accessListJSON,
            getSenderAddress: () => this.from,
            /**
             * the minimum amount of gas the tx must have (DataFee + TxFee + Creation Fee)
             */
            getBaseFee: () => {
                const fee = this.calculateIntrinsicGas();
                return fee + this.accessListDataFee;
            },
            getUpfrontCost: (baseFee = 0n) => {
                const { gas, maxPriorityFeePerGas, maxFeePerGas, value } = this;
                const maxPriorityFeePerGasBI = maxPriorityFeePerGas.toBigInt();
                const maxFeePerGasBI = maxFeePerGas.toBigInt();
                const gasLimitBI = gas.toBigInt();
                const valueBI = value.toBigInt();
                const inclusionFeePerGas = bigIntMin(maxPriorityFeePerGasBI, maxFeePerGasBI - baseFee);
                const gasPrice = inclusionFeePerGas + baseFee;
                return gasLimitBI * gasPrice + valueBI;
            },
            supports: (capability) => {
                return CAPABILITIES.includes(capability);
            }
        };
    }
    /**
     * sign a transaction with a given private key, then compute and set the `hash`.
     *
     * @param privateKey - Must be 32 bytes in length
     */
    signAndHash(privateKey) {
        if (this.v != null) {
            throw new Error("Internal Error: RuntimeTransaction `sign` called but transaction has already been signed");
        }
        const typeBuf = this.type.toBuffer();
        const raw = this.toEthRawTransaction(utils_1.BUFFER_ZERO, utils_1.BUFFER_ZERO, utils_1.BUFFER_ZERO);
        const data = (0, rlp_1.encodeRange)(raw, 1, 9);
        const dataLength = data.length;
        const msgHash = (0, utils_1.keccak)(Buffer.concat([typeBuf, (0, rlp_1.digest)([data.output], dataLength)]));
        const sig = (0, utils_1.ecsign)(msgHash, privateKey);
        this.v = utils_1.Quantity.from(sig.v);
        this.r = utils_1.Quantity.from(sig.r);
        this.s = utils_1.Quantity.from(sig.s);
        raw[10] = this.v.toBuffer();
        raw[11] = this.r.toBuffer();
        raw[12] = this.s.toBuffer();
        this.raw = raw;
        const encodedSignature = (0, rlp_1.encodeRange)(raw, 10, 3);
        // raw data is type concatenated with the rest of the data rlp encoded
        this.serialized = Buffer.concat([
            typeBuf,
            (0, rlp_1.digest)([data.output, encodedSignature.output], dataLength + encodedSignature.length)
        ]);
        this.hash = utils_1.Data.from((0, utils_1.keccak)(this.serialized));
        this.encodedData = data;
        this.encodedSignature = encodedSignature;
    }
    toEthRawTransaction(v, r, s) {
        return [
            this.type.toBuffer(),
            this.chainId.toBuffer(),
            this.nonce.toBuffer(),
            this.maxPriorityFeePerGas.toBuffer(),
            this.maxFeePerGas.toBuffer(),
            this.gas.toBuffer(),
            this.to ? this.to.toBuffer() : utils_1.BUFFER_EMPTY,
            this.value.toBuffer(),
            this.data.toBuffer(),
            this.accessList,
            v,
            r,
            s
        ];
    }
    computeIntrinsics(v, raw) {
        return (0, signing_1.computeIntrinsicsFeeMarketTx)(v, raw);
    }
    updateEffectiveGasPrice(baseFeePerGas) {
        const maxFeePerGas = this.maxFeePerGas.toBigInt();
        const maxPriorityFeePerGas = this.maxPriorityFeePerGas.toBigInt();
        const a = maxFeePerGas - baseFeePerGas;
        const tip = a < maxPriorityFeePerGas ? a : maxPriorityFeePerGas;
        this.effectiveGasPrice = utils_1.Quantity.from(baseFeePerGas + tip);
    }
}
exports.EIP1559FeeMarketTransaction = EIP1559FeeMarketTransaction;
//# sourceMappingURL=eip1559-fee-market-transaction.js.map